import { GameState, GamePhase, RoomId, ItemId, PuzzleId, EndingType, ScreenEffect, DialogMessage } from '../types';
import { ROOMS } from '../data/rooms';

const TOTAL_HOTSPOTS = Object.values(ROOMS).reduce((sum, room) => sum + room.hotspots.length, 0);
const DIARY_ITEMS: ItemId[] = [
  'diary_fragment_1', 'diary_fragment_2', 'diary_fragment_3',
  'diary_fragment_4', 'diary_fragment_5',
];

function createInitialState(): GameState {
  return {
    phase: 'title',
    currentRoom: 'classroom',
    inventory: [],
    solvedPuzzles: [],
    interactedHotspots: [],
    unlockedRooms: [],
    diaryFragmentsFound: 0,
    explorationRate: 0,
    horrorLevel: 0,
    activeDialog: null,
    dialogIndex: 0,
    activePuzzle: null,
    screenEffect: null,
    acquiredItem: null,
    messageLog: [],
    endingType: null,
    playtime: 0,
    isMenuOpen: false,
    bgmVolume: 0.3,
    sfxVolume: 0.5,
  };
}

type Listener = () => void;

class GameStore {
  private state: GameState;
  private listeners: Set<Listener> = new Set();
  private timerInterval: number | null = null;

  constructor() {
    this.state = createInitialState();
    this.loadVolumeSettings();
  }

  private loadVolumeSettings() {
    try {
      const data = localStorage.getItem('horror_escape_volume');
      if (data) {
        const { bgmVolume, sfxVolume } = JSON.parse(data);
        if (typeof bgmVolume === 'number') this.state.bgmVolume = bgmVolume;
        if (typeof sfxVolume === 'number') this.state.sfxVolume = sfxVolume;
      }
    } catch {}
  }

  private saveVolumeSettings() {
    localStorage.setItem('horror_escape_volume', JSON.stringify({
      bgmVolume: this.state.bgmVolume,
      sfxVolume: this.state.sfxVolume,
    }));
  }

  getState(): GameState {
    return this.state;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit() {
    this.listeners.forEach(l => l());
  }

  private update(partial: Partial<GameState>) {
    this.state = { ...this.state, ...partial };
    this.emit();
  }

  // === Phase management ===
  setPhase(phase: GamePhase) {
    this.update({ phase });
    if (phase === 'playing') {
      this.startTimer();
    } else {
      this.stopTimer();
    }
  }

  startGame() {
    const { bgmVolume, sfxVolume } = this.state;
    this.state = createInitialState();
    this.update({ phase: 'prologue', bgmVolume, sfxVolume });
  }

  startPlaying() {
    this.update({ phase: 'playing', currentRoom: 'classroom' });
    this.startTimer();
  }

  // === Room navigation ===
  moveToRoom(roomId: RoomId) {
    const room = ROOMS[roomId];
    if (!room) return;

    if (room.requiredKey && !this.state.unlockedRooms.includes(roomId)) {
      if (!this.state.inventory.includes(room.requiredKey)) {
        this.addMessage(`鍵がかかっている。${room.requiredKey === 'staffroom_key' ? '職員室の鍵' : room.requiredKey === 'music_room_key' ? '音楽室の鍵' : room.requiredKey === 'floor3_key' ? '3階の鍵' : '屋上の鍵'}が必要だ。`);
        return;
      }
      // First unlock: consume the key and record the room as unlocked
      this.removeItem(room.requiredKey);
      this.update({ unlockedRooms: [...this.state.unlockedRooms, roomId] });
    }

    this.update({
      currentRoom: roomId,
      horrorLevel: Math.min(10, this.state.horrorLevel + 0.5),
    });
    this.updateExplorationRate();
  }

  // === Inventory ===
  addItem(itemId: ItemId) {
    if (this.state.inventory.includes(itemId)) return;
    const newInventory = [...this.state.inventory, itemId];
    let diaryCount = this.state.diaryFragmentsFound;
    if (DIARY_ITEMS.includes(itemId)) {
      diaryCount++;
    }
    this.update({
      inventory: newInventory,
      diaryFragmentsFound: diaryCount,
    });
  }

  removeItem(itemId: ItemId) {
    this.update({
      inventory: this.state.inventory.filter(id => id !== itemId),
    });
  }

  hasItem(itemId: ItemId): boolean {
    return this.state.inventory.includes(itemId);
  }

  // === Puzzles ===
  openPuzzle(puzzleId: PuzzleId) {
    this.update({ activePuzzle: puzzleId });
  }

  closePuzzle() {
    this.update({ activePuzzle: null });
  }

  solvePuzzle(puzzleId: PuzzleId) {
    if (this.state.solvedPuzzles.includes(puzzleId)) return;
    this.update({
      solvedPuzzles: [...this.state.solvedPuzzles, puzzleId],
      horrorLevel: Math.min(10, this.state.horrorLevel + 1),
    });
    this.updateExplorationRate();
  }

  isPuzzleSolved(puzzleId: PuzzleId): boolean {
    return this.state.solvedPuzzles.includes(puzzleId);
  }

  // === Hotspot interaction ===
  interactHotspot(hotspotId: string) {
    if (this.state.interactedHotspots.includes(hotspotId)) return;
    this.update({
      interactedHotspots: [...this.state.interactedHotspots, hotspotId],
    });
    this.updateExplorationRate();
  }

  hasInteracted(hotspotId: string): boolean {
    return this.state.interactedHotspots.includes(hotspotId);
  }

  // === Dialog ===
  showDialog(messages: DialogMessage[]) {
    this.update({ activeDialog: messages, dialogIndex: 0 });
  }

  advanceDialog() {
    if (!this.state.activeDialog) return;
    if (this.state.dialogIndex < this.state.activeDialog.length - 1) {
      this.update({ dialogIndex: this.state.dialogIndex + 1 });
    } else {
      this.update({ activeDialog: null, dialogIndex: 0 });
    }
  }

  dismissDialog() {
    this.update({ activeDialog: null, dialogIndex: 0 });
  }

  // === Screen effects ===
  showScreenEffect(effect: ScreenEffect) {
    this.update({ screenEffect: effect });
    setTimeout(() => {
      this.update({ screenEffect: null });
    }, effect.duration);
  }

  // === Acquired item display ===
  showAcquiredItem(itemId: ItemId) {
    this.update({ acquiredItem: itemId });
  }

  dismissAcquiredItem() {
    this.update({ acquiredItem: null });
  }

  // === Messages ===
  addMessage(message: string) {
    this.update({
      messageLog: [...this.state.messageLog.slice(-49), message],
    });
  }

  // === Exploration rate ===
  private updateExplorationRate() {
    const interacted = this.state.interactedHotspots.length;
    const rate = Math.round((interacted / TOTAL_HOTSPOTS) * 100);
    this.update({ explorationRate: Math.min(100, rate) });
  }

  // === Ending ===
  determineEnding(finalChoice: string): EndingType {
    const { explorationRate, diaryFragmentsFound } = this.state;

    let ending: EndingType;
    if (explorationRate >= 80 && diaryFragmentsFound >= 5 && finalChoice === '3') {
      ending = 'ending_c';
    } else if (explorationRate >= 60) {
      ending = 'ending_b';
    } else {
      ending = 'ending_a';
    }

    this.update({ endingType: ending, phase: 'ending' });
    this.stopTimer();
    return ending;
  }

  // === Timer ===
  private startTimer() {
    if (this.timerInterval) return;
    this.timerInterval = window.setInterval(() => {
      this.update({ playtime: this.state.playtime + 1 });
    }, 1000);
  }

  private stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  // === Menu ===
  toggleMenu() {
    this.update({ isMenuOpen: !this.state.isMenuOpen });
  }

  // === Sound ===
  setBgmVolume(volume: number) {
    this.update({ bgmVolume: Math.max(0, Math.min(1, volume)) });
    this.saveVolumeSettings();
  }

  setSfxVolume(volume: number) {
    this.update({ sfxVolume: Math.max(0, Math.min(1, volume)) });
    this.saveVolumeSettings();
  }

  // === Save/Load ===
  saveGame() {
    const saveData = { ...this.state, activeDialog: null, screenEffect: null, activePuzzle: null, acquiredItem: null };
    localStorage.setItem('horror_escape_save', JSON.stringify(saveData));
    this.addMessage('セーブしました。');
  }

  loadGame(): boolean {
    const data = localStorage.getItem('horror_escape_save');
    if (!data) return false;
    try {
      const saved = JSON.parse(data) as GameState;
      this.state = { ...saved, activeDialog: null, screenEffect: null, activePuzzle: null, acquiredItem: null, isMenuOpen: false };
      this.emit();
      if (saved.phase === 'playing') this.startTimer();
      return true;
    } catch {
      return false;
    }
  }

  resetGame() {
    this.stopTimer();
    const { bgmVolume, sfxVolume } = this.state;
    this.state = { ...createInitialState(), bgmVolume, sfxVolume };
    this.emit();
  }
}

export const gameStore = new GameStore();
